import React, { useState, useEffect } from 'react';
import "./index.css";
import { ReactComponent as Close } from "../../../assets/icons/notifications/Cross M.svg";
import CustomSelect from '../../../Components/shared/customSelect';
import DateTimePicker from '../../../Components/shared/datePicker';
import { sendNotification, updateNotification } from "../../../Data/Functions/index";
import { notificationOptions, validateLink, formatToDayTime, NotificationDropDownIcon, normalizeTypeName, notificationTypeIcons } from "../../../Data/notifications";
import { showCustomToast } from "../../../Components/shared/customToast";
import { ReactComponent as EditIcon } from "../../../assets/icons/notifications/edit-button.svg";

const SendNotificationModal = ({ onClose, notificationData = null }) => {

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        link: '',
        type: '',
        isSendNow: false,
        isSheduled: false,
        sheduledDateTime: null
    });

    const [focus, setFocus] = useState({
        title: false,
        description: false,
        link: false
    });

    const [uiState, setUiState] = useState({
        isLoading: false,
        closing: false,
        isPreview: false,
        dateTimeModal: false
    })

    const [notificationState, setNotificationState] = useState({
        selectedType: null,
        singleEditMode: false,
        futureScheduled: false,
        futureStateChanged: false,
        isFutureValid: false
    })

    const [errors, setErrors] = useState({
        link: '',
        title: false
    })

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        if (field === 'link') setErrors(prev => ({ ...prev, link: validateLink(value) }))
        if (field === 'title') setErrors(prev => ({ ...prev, title: value.length > 75 }))
    };

    const handleFocus = (field, value) => setFocus(prev => ({ ...prev, [field]: value }));


    const isFutureScheduleValid = formData.isSheduled
        ? formData.sheduledDateTime && (new Date(formData.sheduledDateTime) > new Date()) : true;

    const isFormValid =
        formData.type &&
        formData.title &&
        formData.description &&
        (formData.isSendNow || formData.isSheduled) &&
        !errors.title &&
        !errors.link &&
        isFutureScheduleValid;

    useEffect(() => {
        if (notificationData) {
            setUiState(prev => ({ ...prev, isPreview: true }))
            setNotificationState(prev => ({ ...prev, singleEditMode: true }))

            const scheduledDate = notificationData.sheduledDateTime
                ? new Date(notificationData.sheduledDateTime)
                : null;

            const matchedType = notificationOptions.find((option) => option.label === notificationData.type);
            setNotificationState(prev => ({ ...prev, selectedType: matchedType }))

            const parsedData = {
                title: notificationData.title || '',
                description: notificationData.description || '',
                link: notificationData.link || '',
                type: matchedType.value || '',
                isSendNow: notificationData.sent || false,
                isSheduled: notificationData.isShedule || false,
                sheduledDateTime: scheduledDate
            };

            setFormData(parsedData);

            const isValidFutureSchedule = notificationData.isShedule
                ? scheduledDate && scheduledDate > new Date()
                : true;

            setNotificationState(prev => ({ ...prev, isFutureValid: isValidFutureSchedule }))
        } else {
            setNotificationState(prev => ({ ...prev, singleEditMode: false }))
        }
    }, [notificationData]);

    const shouldHideButtons = notificationData &&
        (
            (notificationState.singleEditMode && (notificationData.sent === true ||
                (
                    notificationData.sent === false &&
                    notificationData.sheduledDateTime &&
                    new Date(notificationData.sheduledDateTime) < new Date()
                ))
            )
        );

    const isDataChanged = () => {
        const keysToCompare = ['type', 'title', 'description', 'link'];

        const basicChanged = keysToCompare.some(key => {
            if (key === 'type') {
                const originalTypeObj = notificationOptions.find(
                    opt => opt.label === notificationData.type
                );
                const originalTypeValue = originalTypeObj?.value;
                return originalTypeValue !== formData.type;
            }
            return notificationData[key] !== formData[key];
        });

        const isSendNowChanged = (notificationData.sent || false) !== (formData.isSendNow || false);
        const isSheduledChanged = (notificationData.isShedule || false) !== (formData.isSheduled || false);

        const originalDate = notificationData.sheduledDateTime
            ? new Date(notificationData.sheduledDateTime).getTime()
            : null;
        const currentDate = formData.sheduledDateTime
            ? new Date(formData.sheduledDateTime).getTime()
            : null;
        const isDateChanged = originalDate !== currentDate;

        return basicChanged || isSendNowChanged || isSheduledChanged || isDateChanged;
    };

    const handleClose = () => {
        setUiState(prev => ({ ...prev, closing: true }))
        setTimeout(() => onClose(), 300);
    };

    const handleSubmit = async () => {
        setNotificationState(prev => ({ ...prev, selectedType: (notificationOptions.find(option => option.value === formData.type)) }))

        if (notificationState.futureScheduled) {
            const changed = isDataChanged();
            setNotificationState(prev => ({ ...prev, futureStateChanged: changed }))
            setNotificationState(prev => ({ ...prev, singleEditMode: false }))
            const isFutureValid =
                formData.isSheduled &&
                formData.sheduledDateTime &&
                new Date(formData.sheduledDateTime).getTime() > Date.now();

            setNotificationState(prev => ({ ...prev, isFutureValid: isFutureValid }))
            setNotificationState(prev => ({ ...prev, futureScheduled: false }))
        }
        if (!uiState.isPreview) {
            setUiState(prev => ({ ...prev, isPreview: true }))
        }
        else {
            const payload = {
                title: formData.title,
                description: formData.description,
                type: notificationState.selectedType?.label || '',
                isShedule: formData.isSheduled,
                link: formData.link || '',
                sheduledDateTime: formData.isSheduled && formData.sheduledDateTime ? new Date(formData.sheduledDateTime).getTime() : null,
            };

            setUiState(prev => ({ ...prev, isLoading: true }))
            try {
                const notificationIdToUpdate = notificationData?.id;
                if (notificationState.futureScheduled === false && notificationIdToUpdate) {
                    const res = await updateNotification(notificationIdToUpdate, payload);

                    if (res?.success) {
                        setUiState(prev => ({ ...prev, dateTimeModal: false }))
                        showCustomToast("Scheduled notification updated successfully", "success");
                        setTimeout(() => handleClose(), 500);
                    } else {
                        console.warn("Update failed");
                        showCustomToast(
                            res?.message || "Failed to update the notification. Please try again.",
                            "error"
                        );
                    }
                } else {
                    const res = await sendNotification(payload);
                    if (res?.success) {
                        setUiState(prev => ({ ...prev, dateTimeModal: false }))
                        showCustomToast(
                            formData.isSheduled ?
                                "Successfully scheduled notification" :
                                "Notification sent successfully",
                            "success"
                        );
                        setTimeout(() => handleClose(), 500);
                    } else {
                        console.warn('No success response');
                        showCustomToast(
                            res?.message || "Failed to send the notification. Please try again.",
                            "error"
                        );
                    }
                }
            } catch (err) {
                console.error("Error in handleSubmit:", err);
                showCustomToast(err?.message || "An unexpected error occurred.", "error");
            } finally {
                setUiState(prev => ({ ...prev, isLoading: false }))
            }
        }
    };

    const normalizedType = normalizeTypeName(notificationState.selectedType?.label || '');
    const IconComponent = notificationTypeIcons[normalizedType];
    const option = notificationOptions?.find((opt) => opt.value === formData?.type);
    const BlackIcon = NotificationDropDownIcon[option?.value];

    return (
        <div 
            className="modalOverlay" 
            onClick={onClose}
        >
            <div 
                onClick={(e) => e.stopPropagation()} 
                className={`modalPanel sm:w-[540px] w-full ${uiState.closing ? 'slideOut' : 'slideIn'}`}
            >
                <div className='topContainer'>
                    <div className='containerHeader'>
                        <span className='headerText'>Send New Notification</span>
                        <Close className='cursor-pointer' onClick={handleClose} />
                    </div>

                    {uiState.isPreview ? (() => {

                        return (
                            <div className={`notificationPreviewItemContainer`}>
                                {(!(shouldHideButtons) && (
                                    <div className="editIconWrapper">
                                        <EditIcon 
                                            onClick={() => {
                                                setUiState(prev => ({ ...prev, isPreview: false }))
                                                setNotificationState(prev => ({ ...prev, singleEditMode: false }))
                                                if (notificationData) {
                                                    setNotificationState(prev => ({ ...prev, futureScheduled: true }))
                                                    setNotificationState(prev => ({ ...prev, isFutureValid: false }))
                                                }
                                            }} 
                                        />
                                    </div>
                                ))}

                                <div>
                                    {IconComponent && (
                                        <IconComponent className="w-10 h-10" />
                                    )}
                                </div>

                                <div className='notificationContent'>
                                    <div role="Title and type" className='titleTypeNotification'>
                                        <span className={`notificationPreviewTitle`}>{formData.title || 'New Title'}</span>
                                        <div role='icon and type div' className="iconTypeNotification">
                                            <span className=''>
                                                {BlackIcon && (<BlackIcon />)}

                                            </span>
                                            <span className="typeNotification">
                                                {option.label}
                                            </span>
                                        </div>
                                    </div>

                                    <div className='mt-3 mb-3 notificationPreviewDescription' style={{ width: '405px', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                        {formData.description}
                                    </div>
                                    <div className="timeReceiveContent">
                                        <span className='notificationDot'></span>
                                        <span className='notificationTime'>
                                            {formData.isSheduled
                                                ? formatToDayTime(formData?.sheduledDateTime)
                                                : 'Send Now'
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })() : (
                        <div className='containerContent'>
                            {/* Notification Type */}
                            <div className='notificationType'>
                                <span className='notificationTypeText'>Type</span>
                                <CustomSelect
                                    label="Notification Type"
                                    options={notificationOptions}
                                    value={formData.type}
                                    onChange={(value) => handleChange('type', value)}
                                    disabled={uiState.isLoading}
                                />
                            </div>

                            <div className='divider'></div>

                            {/* Title Input */}
                            <div className='notificationTitle'>
                                <span className='notificationTitleText'>Title</span>
                                <div className='relative sm:w-[338px] w-full'>
                                    <label
                                        htmlFor='notification-title'
                                        className={`notificationTitleText absolute left-3 px-1 bg-transparent transition-all duration-200 z-10 ${focus.title || formData.title ? 'label-above' : 'label-in-place'}`}
                                    >
                                        Enter the title
                                    </label>
                                    <div className={`notificationTitleInput ${focus.title || formData.title ? 'border-[#030F23]' : 'border-[#E6E7E9]'} border ${errors.title ? 'border-red-500' : ''}`}>
                                        <input
                                            id='notification-title'
                                            type='text'
                                            value={formData.title}
                                            onChange={(e) => handleChange('title', e.target.value)}
                                            onFocus={() => handleFocus('title', true)}
                                            onBlur={() => handleFocus('title', false)}
                                            className='notificationTitleInputText focus:border-[#030F23] pl-1 w-full focus:outline-none pt-3 pb-0'
                                            disabled={uiState.isLoading}
                                        />
                                    </div>
                                    <div className={`text-xs text-right absolute -bottom-5 right-0 ${errors.title ? 'text-red-500' : 'text-gray-400'}`}>{formData.title.length}/75</div>
                                </div>
                            </div>

                            {/* Description Input */}
                            <div className='notificationDescription'>
                                <span className='notificationDescriptionText'>Description</span>
                                <div className='relative sm:w-[338px] w-full'>
                                    <label
                                        htmlFor='notification-description'
                                        className={`notificationTitleText absolute left-3 px-1 bg-white transition-all duration-200 z-10 ${focus.description || formData.description ? 'label-above' : 'label-in-place'}`}
                                    >
                                        Enter the description
                                    </label>
                                    <div className={`notificationDescriptionInput h-[142px] ${focus.description || formData.description ? 'border-[#030F23]' : 'border-[#E6E7E9]'} border`}>
                                        <textarea
                                            id='notification-description'
                                            className='notificationDescriptionInputText w-full h-full resize-none pt-3 pl-1'
                                            value={formData.description}
                                            onChange={(e) => handleChange('description', e.target.value)}
                                            onFocus={() => handleFocus('description', true)}
                                            onBlur={() => handleFocus('description', false)}
                                            disabled={uiState.isLoading}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Link Input */}
                            <div className='notificationLink'>
                                <div className='flex items-center gap-1'>
                                    <span className='notificationLinkText'>Link</span>
                                    <span className='text-gray-400 text-xs'>(optional)</span>
                                </div>
                                <div className='relative sm:w-[338px] w-full'>
                                    <label
                                        htmlFor='notification-link'
                                        className={`notificationLinkText absolute left-3 px-1 bg-transparent transition-all duration-200 z-10 ${focus.link || formData.link ? 'label-above' : 'label-in-place'}`}
                                    >
                                        Add external link
                                    </label>
                                    <div className={`notificationTitleInput border ${focus.link || formData.link ? 'border-[#030F23]' : 'border-[#E6E7E9]'} ${errors.link ? 'border-red-500' : ''}`}>
                                        <input
                                            id='notification-link'
                                            type='text'
                                            value={formData.link}
                                            onChange={(e) => handleChange('link', e.target.value)}
                                            onFocus={() => handleFocus('link', true)}
                                            onBlur={() => handleFocus('link', false)}
                                            className='notificationLinkInputText focus:border-[#030F23] pl-1 w-full focus:outline-none pt-3 pb-0'
                                            disabled={uiState.isLoading}
                                        />
                                    </div>
                                    {errors.link && <div className="text-red-500 text-xs absolute -bottom-5 right-0">{errors.link}</div>}
                                </div>
                            </div>

                            <div className='divider'></div>

                            {/* Scheduling Controls */}
                            <div className='notificationSheduling'>
                                <span className='notificationSheduleText'>Scheduling</span>
                                <div className='flex flex-col gap-4 sm:w-[338px] w-full'>
                                    {/* Send Now */}
                                    <div className={`notificationSheduleInput border ${!formData.isSendNow ? 'border-[#E6E7E9]' : 'border-[#12ABBF]'}`}>
                                        <input
                                            type='checkbox'
                                            className='hidden'
                                            checked={formData.isSendNow}
                                            id="sendNow"
                                            onChange={(e) => handleChange('isSendNow', e.target.checked ? true : false)}
                                        />
                                        <label htmlFor="sendNow" className="cursor-pointer">
                                            <div className={`w-[20px] h-[20px] rounded-[4px] flex items-center justify-center ${formData.isSendNow ? 'bg-[#12ABBF]' : 'border border-[#9A9FA7]'}`}>
                                                {formData.isSendNow && <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#12ABBF" /><path d="M5.333 10.667 8 13.334l6.667-6.667" stroke="#fff" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                                            </div>
                                        </label>
                                        <span className='notificationSendNowLabelText'>Send Now</span>
                                    </div>

                                    {/* Schedule */}
                                    <div
                                        className={`notificationSheduleInput border relative ${!formData.isSheduled ? 'border-[#E6E7E9]' : 'border-[#12ABBF]'}`}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (formData.isSheduled && formData.sheduledDateTime) {
                                                setUiState(prev => ({ ...prev, dateTimeModal: !uiState.dateTimeModal }));
                                            }
                                        }}
                                    >
                                        {formData.isSheduled && uiState.dateTimeModal && (
                                            <div 
                                                className={`datepicker-animate-up ${uiState.dateTimeModal ? 'block' : 'hidden'}`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <DateTimePicker
                                                    value={formData.sheduledDateTime}
                                                    onApply={(dateTime) => {
                                                        handleChange('sheduledDateTime', dateTime);
                                                        handleChange('isSheduled', true);
                                                        // setDateTimeModal(false);
                                                        setUiState(prev => ({ ...prev, dateTimeModal: false }))

                                                    }}
                                                    onClear={() => {
                                                        handleChange('sheduledDateTime', null);
                                                        handleChange('isSheduled', false);
                                                        // setDateTimeModal(false);
                                                        setUiState(prev => ({ ...prev, dateTimeModal: false }))
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            id="shedule"
                                            checked={formData.isSheduled}
                                            onChange={(e) => {
                                                handleChange('isSheduled', e.target.checked);
                                                // setDateTimeModal(e.target.checked);
                                                setUiState(prev => ({ ...prev, dateTimeModal: e.target.checked }))
                                                if (e.target.checked) handleChange('isSendNow', false);
                                            }}
                                        />
                                        <label htmlFor="shedule" className="cursor-pointer">
                                            <div className={`w-[20px] h-[20px] rounded-[4px] flex items-center justify-center ${formData.isSheduled ? 'bg-[#12ABBF]' : 'border border-[#9A9FA7]'}`}>
                                                {formData.isSheduled && <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#12ABBF" /><path d="M5.333 10.667 8 13.334l6.667-6.667" stroke="#fff" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                                            </div>
                                        </label>
                                        <div className="notificationScheduleLabel">
                                            <span className={formData.sheduledDateTime ? 'scheduleLabelActive' : 'notificationSheduleLabelText'}>Schedule</span>
                                            {formData.sheduledDateTime && (
                                                <div className="sheduledDateTimeText">
                                                    {`${formData.sheduledDateTime.toLocaleDateString('en-GB')} > ${formData.sheduledDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                {(!(shouldHideButtons) && (
                    <div className='btnContainer flex items-center gap-2.5 sm:w-auto w-full mt-4'>
                        <button 
                            onClick={handleClose} 
                            className='notificationCancelBtn notificationCancelBtnText'
                        >
                            Cancel
                        </button>
                        <button
                            onClick={isFormValid && !uiState.isLoading ? handleSubmit : null}
                            disabled={
                                !isFormValid ||
                                uiState.isLoading ||
                                (notificationState.isFutureValid && !notificationState.futureStateChanged)
                            }
                            className={`notificationSendBtn notificationSendBtnText ${isFormValid &&
                                !uiState.isLoading &&
                                (!notificationState.isFutureValid || notificationState.futureStateChanged)
                                ? 'active'
                                : 'disabled'
                                }`}
                        >
                            {uiState.isLoading ? "Sending..." : "Send"}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SendNotificationModal;
